/**
 * What a bill card says (v8.0.0, Blueprint §9). PURE, so the wording is tested
 * without a browser.
 *
 * Rules carried over from the deposit card: a question, not a verdict ("Track it
 * as a bill?"); nothing pre-selected; an amount the bank did not report is an em
 * dash with its reason spoken, never $0; no scolding.
 */

import type { ApiBill, ApiPaidBill } from './api';
import { dueText } from './due-date';
import { formatMoney, formatMoneyExact, UNKNOWN_AMOUNT } from './envelope-math';
import { shortDate } from './pace';

export function cadenceText(cadence: string): string {
  switch (cadence) {
    case 'weekly':
      return 'Every week';
    case 'biweekly':
      return 'Every two weeks';
    case 'semi_monthly':
      return 'Twice a month';
    case 'monthly':
      return 'Every month';
    case 'annually':
      return 'Once a year';
    default:
      // Plaid has no quarterly cadence; a quarterly bill lands here.
      return 'Repeats on no fixed schedule';
  }
}

export interface BillCardCopy {
  label: string;
  amountText: string;
  amountLabel: string;
  route: string;
  /** The bank's own text, when it says something the name does not. */
  bankText: string | null;
  provisionalNote: string | null;
  question: string;
  trackLabel: string;
  trackAria: string;
  dismissLabel: string;
  dismissAria: string;
}

export function billCardCopy(bill: ApiBill, now: Date = new Date()): BillCardCopy {
  const known = bill.amountMinor != null;
  const next = bill.nextDueDate ? dueText(bill.nextDueDate, now) : null;
  const when = next
    ? `next ${next.replace(/^due /, '')}`
    : `last paid ${shortDate(bill.lastDate, now)}`;
  const account = bill.accountMask ? `${bill.accountName} ··${bill.accountMask}` : bill.accountName;
  const sameText = bill.description.trim().toLowerCase() === bill.name.trim().toLowerCase();
  return {
    label: 'Looks like a bill',
    amountText: known ? formatMoney(bill.amountMinor) : UNKNOWN_AMOUNT,
    amountLabel: known
      ? `${formatMoneyExact(bill.amountMinor)} to ${bill.name}`
      : `${bill.name}, amount not reported by your bank`,
    route: `${bill.name} · ${cadenceText(bill.cadence).toLowerCase()} · ${when}`,
    bankText: sameText ? null : `${bill.description} · ${account}`,
    provisionalNote: bill.provisional
      ? 'Seen fewer than three times, so it may not repeat yet.'
      : null,
    question: known
      ? 'Track it as a bill?'
      : "Track it as a bill? Your bank didn't say what it costs.",
    trackLabel: known ? 'Track it' : 'Add the amount',
    trackAria: known
      ? `Track ${bill.name} as a bill of ${formatMoneyExact(bill.amountMinor)}`
      : `Track ${bill.name} as a bill: enter what it costs`,
    dismissLabel: 'Not a bill',
    dismissAria: `${bill.name} is not a bill. Don't ask again.`,
  };
}

export interface PaidCardCopy {
  label: string;
  title: string;
  detail: string;
  action: string;
  actionAria: string;
}

export function paidCardCopy(paid: ApiPaidBill, now: Date = new Date()): PaidCardCopy {
  const on = shortDate(paid.paidOn, now);
  const nextDate = paid.nextDueDate ? `the next one due ${shortDate(paid.nextDueDate, now)}` : null;
  const nextAmount = paid.nextTargetMinor != null ? ` (${formatMoney(paid.nextTargetMinor)})` : '';
  const roll = nextDate
    ? `set ${nextDate}${nextAmount}`
    : "clear its date — your bank hasn't said when the next one is";
  const detail =
    paid.clearMinor > 0
      ? `${formatMoney(paid.clearMinor)} set aside for it is spent. Move it back to free cash and ${roll}.`
      : `Nothing was set aside for it this time. ${roll.charAt(0).toUpperCase()}${roll.slice(1)}.`;
  const action = paid.clearMinor > 0 ? 'Clear it and roll forward' : 'Roll forward';
  return {
    label: 'Paid',
    title: `${paid.envelopeName} was paid ${on}`,
    detail,
    action,
    actionAria: `${action}: ${paid.envelopeName}`,
  };
}

export interface ForwardCopy {
  heading: string;
  summary: string;
}

/** The forward view's heading and one-line summary, percentage-framed. */
export function forwardCopy(view: {
  dueMinor: number;
  percentSetAside: number | null;
}): ForwardCopy {
  return {
    heading: 'Coming up · next 30 days',
    summary:
      view.percentSetAside == null
        ? `${formatMoney(view.dueMinor)} due. How much is set aside is waiting on your bank.`
        : `${formatMoney(view.dueMinor)} due · ${view.percentSetAside}% set aside`,
  };
}
